import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import html2canvas from "html2canvas";
import Grafica from "../../componentes/Grafica";
import clienteAxios from "../../axios";
import Dasboard from "../dasboard/dashboard";
import Cargador from "../../componentes/Cargador";
import EsqueletoGrafica from "../../componentes/EsqueletoGrafica";
import OverlayProceso from "../../componentes/OverlayProceso";
import ModalEnviarCorreo from "../../componentes/ModalEnviarCorreo";

/**
 * Resultados de la encuesta.
 *
 * El flujo está guiado en tres pasos porque antes no era evidente que había
 * que filtrar por fechas ANTES de elegir la pregunta: el botón "Filtrar por
 * fechas" solo recargaba el listado de preguntas, así que cambiar el rango con
 * una pregunta ya seleccionada dejaba la gráfica mostrando el periodo anterior.
 * Ahora el rango es el estado que manda: al cambiarlo se vuelve a consultar
 * todo, incluida la gráfica.
 */

/** `YYYY-MM-DD` en hora local. `toISOString()` cambia de día por el offset UTC. */
const aISO = (fecha) =>
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}-${String(
    fecha.getDate()
  ).padStart(2, "0")}`;

const hoyISO = () => aISO(new Date());

const haceDias = (dias) => {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() - dias);
  return aISO(fecha);
};

const inicioDeMes = () => {
  const fecha = new Date();
  fecha.setDate(1);
  return aISO(fecha);
};

/** Rangos rápidos: cubren el 90% de las consultas sin tocar los calendarios. */
const RANGOS = [
  { clave: "hoy", etiqueta: "Hoy", calcular: () => ({ desde: hoyISO(), hasta: hoyISO() }) },
  { clave: "7d", etiqueta: "Últimos 7 días", calcular: () => ({ desde: haceDias(6), hasta: hoyISO() }) },
  { clave: "30d", etiqueta: "Últimos 30 días", calcular: () => ({ desde: haceDias(29), hasta: hoyISO() }) },
  { clave: "mes", etiqueta: "Este mes", calcular: () => ({ desde: inicioDeMes(), hasta: hoyISO() }) },
  { clave: "90d", etiqueta: "Últimos 90 días", calcular: () => ({ desde: haceDias(89), hasta: hoyISO() }) },
];

const RANGO_INICIAL = RANGOS.find((rango) => rango.clave === "30d");

/** `2026-08-13` → `13/08/2026`. */
const fechaLegible = (valor) => {
  const partes = String(valor ?? "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : valor || "";
};

const Graficas = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [abiertas, setAbiertas] = useState({});
  const [totalEncuesta, setTotalEncuesta] = useState(0);
  const [idPregunta, setIdPregunta] = useState("");
  const [pregunta, setPregunta] = useState("");

  // `filtros` es el rango aplicado; los `borrador*` son lo que se está
  // escribiendo en los calendarios y todavía no se confirma.
  const [filtros, setFiltros] = useState(RANGO_INICIAL.calcular);
  const [rangoActivo, setRangoActivo] = useState(RANGO_INICIAL.clave);
  const [borradorDesde, setBorradorDesde] = useState(filtros.desde);
  const [borradorHasta, setBorradorHasta] = useState(filtros.hasta);

  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);
  const [cargandoGrafica, setCargandoGrafica] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [verAbiertas, setVerAbiertas] = useState(false);

  // Operación bloqueante en curso (captura / envío). `null` = ninguna.
  const [proceso, setProceso] = useState(null);
  const enviandoCorreo = proceso?.clave === "correo";

  const divRef = useRef();

  /**
   * El SP compara contra columnas `datetime`. Mandar `fechaFin` como
   * `YYYY-MM-DD` la convierte a las 00:00:00 y deja fuera todo lo capturado
   * ese día, que es justo el día que más se consulta.
   */
  const rangoParaApi = useCallback(
    () => ({
      fechaInicio: filtros.desde || null,
      fechaFin: filtros.hasta ? `${filtros.hasta} 23:59:59` : null,
    }),
    [filtros]
  );

  const consultarCatalogo = useCallback(async () => {
    setCargandoCatalogo(true);
    setError("");

    try {
      const { data } = await clienteAxios.post("/api/sp", {
        sp: "graficas",
        op: "preguntas",
        ...rangoParaApi(),
      });

      setPreguntas(
        data.data[0].map(({ idpregunta, pregunta: texto }) => ({
          value: String(idpregunta),
          label: texto,
        }))
      );

      setAbiertas(
        data.data[1].reduce((acumulado, elemento) => {
          const { pregunta: texto, respuesta, correo, folio, fecha, Id } = elemento;
          if (!acumulado[texto]) acumulado[texto] = [];
          acumulado[texto].push({ respuesta, correo, folio, fecha, Id });
          return acumulado;
        }, {})
      );
    } catch (problema) {
      console.error("Error al consultar preguntas:", problema);
      setError("No fue posible cargar las preguntas. Revisa tu conexión e inténtalo de nuevo.");
    } finally {
      setCargandoCatalogo(false);
    }
  }, [rangoParaApi]);

  const consultarResultados = useCallback(
    async (id) => {
      setCargandoGrafica(true);
      setError("");

      try {
        const { data } = await clienteAxios.post("/api/sp", {
          sp: "graficas",
          op: "respuesta",
          idPregunta: id,
          ...rangoParaApi(),
        });

        let total = 0;
        const datos = data.data[0].reduce((acumulado, { respuesta, total: cantidad }) => {
          acumulado[respuesta] = (acumulado[respuesta] || 0) + cantidad;
          total += cantidad;
          return acumulado;
        }, {});

        setRespuestas(datos);
        setTotalEncuesta(total);
      } catch (problema) {
        console.error("Error al consultar resultados:", problema);
        setError("No fue posible cargar los resultados de esta pregunta.");
        setRespuestas({});
        setTotalEncuesta(0);
      } finally {
        setCargandoGrafica(false);
      }
    },
    [rangoParaApi]
  );

  useEffect(() => {
    consultarCatalogo();
  }, [consultarCatalogo]);

  // Cambiar el rango con una pregunta ya elegida vuelve a graficarla sola.
  useEffect(() => {
    if (!idPregunta) {
      setRespuestas({});
      setTotalEncuesta(0);
      return;
    }
    consultarResultados(idPregunta);
  }, [idPregunta, consultarResultados]);

  const aplicarRango = (clave) => {
    const rango = RANGOS.find((item) => item.clave === clave);
    if (!rango) return;

    const nuevo = rango.calcular();
    setRangoActivo(clave);
    setBorradorDesde(nuevo.desde);
    setBorradorHasta(nuevo.hasta);
    setFiltros(nuevo);
    setAviso(null);
  };

  const aplicarPersonalizado = () => {
    if (borradorDesde && borradorHasta && borradorDesde > borradorHasta) {
      setError("La fecha de inicio no puede ser posterior a la fecha final.");
      return;
    }

    setError("");
    setRangoActivo("personalizado");
    setFiltros({ desde: borradorDesde, hasta: borradorHasta });
    setAviso(null);
  };

  const cambioPregunta = (evento) => {
    const { value, selectedIndex, options } = evento.target;
    setIdPregunta(value);
    setPregunta(value ? options[selectedIndex].text : "");
    setAviso(null);
  };

  /** Captura la tarjeta del reporte. Devuelve un data URI JPEG. */
  const capturarGrafica = async () => {
    if (!divRef.current) return null;

    const lienzo = await html2canvas(divRef.current, { scale: 2, backgroundColor: "#ffffff" });
    return lienzo.toDataURL("image/jpeg", 0.92);
  };

  const descargar = async () => {
    setProceso({
      clave: "descarga",
      titulo: "Generando imagen…",
      detalle: "Preparando la captura de la gráfica.",
    });

    try {
      const imagen = await capturarGrafica();
      if (!imagen) return;

      const enlace = document.createElement("a");
      enlace.href = imagen;
      enlace.download = `encuesta-${hoyISO()}.jpg`;
      enlace.click();
    } catch (problema) {
      console.error("Error al generar la captura:", problema);
      setAviso({ tipo: "error", texto: "No fue posible generar la imagen de la gráfica." });
    } finally {
      setProceso(null);
    }
  };

  const enviarPorCorreo = async ({ destinatarios, asunto, mensaje }) => {
    setProceso({
      clave: "correo",
      titulo: "Generando imagen…",
      detalle: "Preparando la captura de la gráfica.",
    });

    try {
      const imagen = await capturarGrafica();

      setProceso({
        clave: "correo",
        titulo: "Enviando correo…",
        detalle: `Enviando el reporte a ${destinatarios.length} destinatario(s).`,
      });

      const { data } = await clienteAxios.post("/api/v1/reportes/grafica", {
        destinatarios,
        asunto,
        mensaje,
        pregunta,
        fechaInicio: filtros.desde || undefined,
        fechaFin: filtros.hasta || undefined,
        totalEncuestas: totalEncuesta,
        resultados: Object.entries(respuestas).map(([respuesta, total]) => ({ respuesta, total })),
        imagen,
      });

      setModalAbierto(false);
      setAviso({
        tipo: data.mailSent ? "exito" : "error",
        texto: data.mailSent
          ? `Reporte enviado a ${destinatarios.join(", ")}.`
          : data.mensaje || "El servidor no envió el correo.",
      });

      return true;
    } catch (problema) {
      console.error("Error al enviar el reporte:", problema);

      const respuesta = problema.response?.data;
      const detalle = Array.isArray(respuesta?.details) ? ` (${respuesta.details.join(", ")})` : "";

      setAviso({
        tipo: "error",
        texto: `${respuesta?.message || "No fue posible enviar el correo."}${detalle}`,
      });

      return false;
    } finally {
      setProceso(null);
    }
  };

  const periodoLegible = useMemo(() => {
    if (!filtros.desde && !filtros.hasta) return "Último mes";
    if (filtros.desde && !filtros.hasta) return `Desde el ${fechaLegible(filtros.desde)}`;
    if (!filtros.desde && filtros.hasta) return `Hasta el ${fechaLegible(filtros.hasta)}`;
    if (filtros.desde === filtros.hasta) return fechaLegible(filtros.desde);
    return `${fechaLegible(filtros.desde)} – ${fechaLegible(filtros.hasta)}`;
  }, [filtros]);

  const hayDatos = Object.keys(respuestas).length > 0;
  const temasAbiertos = Object.keys(abiertas);

  const grafica = {
    labels: Object.keys(respuestas),
    datasets: [
      {
        label: "Opiniones",
        data: Object.values(respuestas),
        backgroundColor: "#3B82F6",
        borderColor: "#2563EB",
        borderWidth: 2,
      },
    ],
  };

  const opciones = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
      datalabels: {
        color: "black",
        anchor: "end",
        align: "top",
        font: { weight: "bold" },
      },
    },
  };

  return (
    <div className="min-h-full">
      <Dasboard />

      <main className="-mt-32">
        <div className="mx-auto max-w-7xl sm:px-4 lg:px-8 min-h-screen">
          <div className="min-h-screen rounded-lg bg-white px-5 py-6 shadow sm:px-6">
            <header className="mb-6 mt-2 text-center">
              <h2 className="text-3xl font-bold">Resultados de la encuesta</h2>
              <p className="mt-2 text-gray-500">
                Elige un periodo, selecciona una pregunta y comparte la gráfica.
              </p>
            </header>

            {error && (
              <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 font-medium text-red-700" role="alert">
                {error}
              </p>
            )}

            {aviso && (
              <div
                className={`mb-4 flex items-start justify-between gap-4 rounded-lg px-4 py-3 font-medium ${
                  aviso.tipo === "exito" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"
                }`}
                role="status"
              >
                <span>{aviso.texto}</span>
                <button
                  type="button"
                  onClick={() => setAviso(null)}
                  className="text-xl leading-none opacity-60 hover:opacity-100"
                  aria-label="Cerrar aviso"
                >
                  &times;
                </button>
              </div>
            )}

            {/* ---------------------------- Paso 1 ---------------------------- */}
            <section className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <h3 className="flex items-center gap-2 font-bold text-gray-800">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-sm text-white">
                  1
                </span>
                Elige el periodo
              </h3>

              <div className="mt-3 flex flex-wrap gap-2">
                {RANGOS.map(({ clave, etiqueta }) => (
                  <button
                    key={clave}
                    type="button"
                    onClick={() => aplicarRango(clave)}
                    className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                      rangoActivo === clave
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:text-blue-600"
                    }`}
                  >
                    {etiqueta}
                  </button>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end">
                <div className="w-full md:w-auto">
                  <label htmlFor="fecha-inicio" className="block text-sm font-bold text-gray-700">
                    Fecha inicio
                  </label>
                  <input
                    id="fecha-inicio"
                    type="date"
                    max={borradorHasta || undefined}
                    value={borradorDesde}
                    onChange={(evento) => setBorradorDesde(evento.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="w-full md:w-auto">
                  <label htmlFor="fecha-fin" className="block text-sm font-bold text-gray-700">
                    Fecha fin
                  </label>
                  <input
                    id="fecha-fin"
                    type="date"
                    min={borradorDesde || undefined}
                    value={borradorHasta}
                    onChange={(evento) => setBorradorHasta(evento.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={aplicarPersonalizado}
                  className="rounded-lg bg-gray-800 px-5 py-2 font-medium text-white hover:bg-gray-900"
                >
                  Aplicar fechas
                </button>
              </div>

              <p className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                Periodo aplicado: <strong className="text-gray-900">{periodoLegible}</strong>
                {cargandoCatalogo && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
                    Actualizando…
                  </span>
                )}
              </p>
            </section>

            {/* ---------------------------- Paso 2 ---------------------------- */}
            <section className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <h3 className="flex items-center gap-2 font-bold text-gray-800">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-sm text-white">
                  2
                </span>
                Selecciona la pregunta
              </h3>

              {cargandoCatalogo && !preguntas.length ? (
                <div className="mt-3 rounded-lg border border-gray-200 bg-white py-6">
                  <Cargador tamano="sm" texto="Cargando las preguntas de la encuesta…" />
                </div>
              ) : (
                <select
                  value={idPregunta}
                  onChange={cambioPregunta}
                  disabled={cargandoCatalogo || !preguntas.length}
                  className="mt-3 w-full rounded-lg border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">
                    {preguntas.length ? "Seleccione una pregunta…" : "No hay preguntas disponibles"}
                  </option>
                  {preguntas.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              )}

              <p className="mt-2 text-sm text-gray-500">
                La gráfica se actualiza sola al cambiar la pregunta o el periodo.
              </p>
            </section>

            {/* ---------------------------- Paso 3 ---------------------------- */}
            <section className="rounded-xl border border-gray-200 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="flex items-center gap-2 font-bold text-gray-800">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-sm text-white">
                    3
                  </span>
                  Revisa y comparte
                </h3>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={descargar}
                    disabled={!hayDatos || cargandoGrafica || Boolean(proceso)}
                    className="rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    Descargar imagen
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalAbierto(true)}
                    disabled={!hayDatos || cargandoGrafica || Boolean(proceso)}
                    className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    Enviar por correo
                  </button>
                </div>
              </div>

              {!idPregunta && (
                <p className="mt-6 rounded-lg border-2 border-dashed border-gray-200 px-4 py-12 text-center text-gray-500">
                  Selecciona una pregunta en el paso 2 para ver su gráfica.
                </p>
              )}

              {idPregunta && cargandoGrafica && (
                <EsqueletoGrafica texto={`Consultando "${pregunta}" · ${periodoLegible}`} />
              )}

              {idPregunta && !cargandoGrafica && !hayDatos && (
                <p className="mt-6 rounded-lg border-2 border-dashed border-amber-200 bg-amber-50 px-4 py-12 text-center text-amber-800">
                  No hay respuestas para esta pregunta en el periodo {periodoLegible}. Prueba con un
                  rango más amplio.
                </p>
              )}

              {idPregunta && !cargandoGrafica && hayDatos && (
                <div ref={divRef} className="mt-4 rounded-lg bg-white p-4 md:p-8">
                  <div className="mb-4 flex flex-col gap-1 border-b pb-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Pregunta</p>
                      <p className="text-lg font-bold text-gray-900">{pregunta}</p>
                    </div>
                    <div className="sm:text-right">
                      <p className="text-sm text-gray-600">
                        Periodo: <strong>{periodoLegible}</strong>
                      </p>
                      <p className="text-sm text-gray-600">
                        Total de respuestas: <strong>{totalEncuesta}</strong>
                      </p>
                    </div>
                  </div>

                  <Grafica ventas={grafica} options={opciones} />
                </div>
              )}
            </section>

            {/* ----------------------- Preguntas abiertas ---------------------- */}
            {temasAbiertos.length > 0 && (
              <section className="mt-6 rounded-xl border border-gray-200">
                <button
                  type="button"
                  onClick={() => setVerAbiertas((valor) => !valor)}
                  className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left font-bold text-gray-800 hover:bg-gray-50"
                  aria-expanded={verAbiertas}
                >
                  <span>
                    Respuestas abiertas
                    <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-sm font-medium text-blue-700">
                      {temasAbiertos.length} pregunta(s)
                    </span>
                  </span>
                  <span className="text-xl text-gray-400">{verAbiertas ? "−" : "+"}</span>
                </button>

                {verAbiertas && (
                  <div className="space-y-6 border-t px-4 py-4">
                    {temasAbiertos.map((tema) => (
                      <div key={tema}>
                        <h4 className="mb-2 font-bold text-gray-900">
                          {tema}
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({abiertas[tema].length} respuestas)
                          </span>
                        </h4>

                        <ul className="max-h-96 space-y-3 overflow-y-auto rounded-lg border bg-gray-50 p-3">
                          {abiertas[tema].map((item, indice) => (
                            <li key={`${item.Id}-${indice}`} className="rounded-lg bg-white p-3 shadow-sm">
                              <p className="mb-2 flex flex-wrap gap-x-3 text-xs text-gray-500">
                                <span>Folio: {item.folio}</span>
                                <span>{String(item.fecha ?? "").split("T")[0]}</span>
                                <span>{item.correo}</span>
                                <a
                                  className="font-medium text-blue-600 hover:underline"
                                  href={`https://app.parquemonchis.com/premio_ganador/?id=${item.Id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Ver encuesta
                                </a>
                              </p>
                              <p className="text-gray-800">{item.respuesta}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </main>

      <OverlayProceso
        visible={Boolean(proceso)}
        titulo={proceso?.titulo}
        detalle={proceso?.detalle}
      />

      <ModalEnviarCorreo
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
        onEnviar={enviarPorCorreo}
        enviando={enviandoCorreo}
        asuntoSugerido={pregunta ? `Reporte de encuesta: ${pregunta}` : "Reporte de encuesta"}
        resumen={
          <>
            <strong>{pregunta}</strong>
            <br />
            {periodoLegible} · {totalEncuesta} respuestas
          </>
        }
      />
    </div>
  );
};

export default Graficas;
